"use client";
import { Eye, EyeOff, Lock, Mail } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useState } from "react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Please enter both email and password.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setError("");
    setIsLoading(true);

    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || "Authentication failed");
        setIsLoading(false);
        return;
      }

      // Login successful
      router.push("/dashboard");
    } catch (error) {
      console.error("Login error:", error);
      setError("An error occurred. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col">
      <header className="w-full flex items-center justify-start py-4 px-8 border-b border-gray-800 bg-gray-950">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Image
              src="/blackgrid-logo.png"
              alt="BlackGrid Logo"
              width={40}
              height={40}
              className="h-10 w-auto"
              onError={(e) => {
                const img = e.currentTarget;
                img.style.display = "none";
                const parent = img.parentElement;
                if (parent) {
                  const fallbackDiv = document.createElement("div");
                  fallbackDiv.className =
                    "h-10 w-10 bg-gray-800 rounded-lg flex items-center justify-center border border-gray-700";
                  fallbackDiv.innerHTML =
                    '<span class="font-bold text-white">BG</span>';
                  parent.appendChild(fallbackDiv);
                }
              }}
              priority
            />
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-2xl text-white tracking-tight">
              BlackGrid
            </span>
            <span className="text-xs text-gray-400">Enterprise Security</span>
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center p-4 bg-gray-950">
        <div className="relative w-full max-w-md">
          <form
            onSubmit={handleSubmit}
            className="bg-gray-900 rounded-lg shadow-xl p-8 w-full flex flex-col gap-6 border border-gray-800"
          >
            {/* Form header */}
            <div className="flex flex-col items-center mb-6">
              <div className="p-3 rounded-lg bg-gray-800 mb-4 border border-gray-700">
                <Lock className="h-6 w-6 text-blue-500" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">
                Welcome Back
              </h1>
              <p className="text-gray-400">Sign in to your account</p>
            </div>

            {/* Email field */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="email"
                className="text-gray-300 text-sm font-medium"
              >
                Email Address
              </label>
              <div className="relative">
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded bg-black border border-gray-700 px-4 py-3 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 pl-11 transition-colors"
                  placeholder="Enter your email"
                  autoComplete="username"
                  disabled={isLoading}
                />
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <Mail className="h-5 w-5 text-gray-500" />
                </div>
              </div>
            </div>

            {/* Password field */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <label
                  htmlFor="password"
                  className="text-gray-300 text-sm font-medium"
                >
                  Password
                </label>
                <a
                  href="#"
                  className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                >
                  Forgot password?
                </a>
              </div>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded bg-black border border-gray-700 px-4 py-3 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 pl-11 pr-11 transition-colors"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  disabled={isLoading}
                />
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <Lock className="h-5 w-5 text-gray-500" />
                </div>
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Error message */}
            {error && (
              <div className="flex items-center gap-2 p-3 rounded bg-red-900/20 border border-red-800">
                <div className="h-2 w-2 bg-red-500 rounded-full"></div>
                <span className="text-red-300 text-sm">{error}</span>
              </div>
            )}

            {/* Login button */}
            <button
              type="submit"
              disabled={isLoading}
              className="relative bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3.5 rounded transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden mt-2"
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Signing in...</span>
                </div>
              ) : (
                <span>Sign In</span>
              )}
            </button>

            {/* Divider */}
            <div className="relative my-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-800"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="px-3 bg-gray-900 text-gray-500">
                  Secure Connection
                </span>
              </div>
            </div>

            {/* Additional info */}
            <div className="text-center">
              <p className="text-gray-500 text-xs">
                By signing in, you agree to our{" "}
                <a href="#" className="text-blue-400 hover:text-blue-300">
                  Terms of Service
                </a>{" "}
                and{" "}
                <a href="#" className="text-blue-400 hover:text-blue-300">
                  Privacy Policy
                </a>
              </p>
            </div>
          </form>

          {/* Security badge */}
          <div className="flex items-center justify-center gap-2 mt-6 text-gray-500 text-sm">
            <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
            <span>All systems secure • TLS 1.3 encrypted</span>
          </div>
        </div>
      </div>

      {/* Simple footer */}
      <footer className="w-full py-6 text-center text-gray-600 text-sm border-t border-gray-800 bg-gray-950">
        <div className="container mx-auto px-4">
          <p>
            © {new Date().getFullYear()} BlackGrid Security. All rights
            reserved.
          </p>
          <p className="text-xs mt-1 text-gray-700">v2.4.1</p>
        </div>
      </footer>
    </div>
  );
}
